import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ModalOpenProvider } from './contexts/ModalOpenContext';
import { AuthShell } from './components/Auth/AuthShell';
import { ProtectedRoute } from './components/Auth/ProtectedRoute';
import ApprovePage from './pages/ApprovePage';
import RejectPage from './pages/RejectPage';
import RevisionUploadPage from './pages/RevisionUploadPage';
import ProjectSchedulePage from './pages/ProjectSchedulePage';
import ProjectPlanningPage from './pages/ProjectPlanningPage';
import ProjectExecutionPage from './pages/ProjectExecutionPage';
import ProjectBuilderPage from './pages/ProjectBuilderPage';
import { AuthCallback } from './pages/AuthCallback';
import { UnauthorizedPage } from './pages/UnauthorizedPage';
import { NoRolePage } from './pages/NoRolePage';
import MainDashboardPage from './pages/MainDashboardPage';
import CreateActionPage from './pages/CreateActionPage';
import ReviewAcknowledgePage from './pages/ReviewAcknowledgePage';
import { TemplateListPage } from './pages/TemplateListPage';
import { TemplateEditPage } from './pages/TemplateEditPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { AirportManagementPage } from './pages/AirportManagementPage';
import { SharePointLibrariesPage } from './pages/Admin/SharePointLibrariesPage';
import { SystemStatsPage } from './pages/Admin/SystemStatsPage';
import { SystemHealthPage } from './pages/Admin/SystemHealthPage';
import { HolidayManagementPage } from './pages/HolidayManagementPage';
import { ProjectAuditReportPage } from './pages/ProjectAuditReportPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ModalOpenProvider>
          <AuthShell>
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Error pages */}
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/no-role" element={<NoRolePage />} />
            
            {/* Protected routes */}
            {/* Root path redirects to /main */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Navigate to="/main" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/approval/approve"
              element={
                <ProtectedRoute>
                  <ApprovePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/approval/reject"
              element={
                <ProtectedRoute>
                  <RejectPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/revision/upload"
              element={
                <ProtectedRoute>
                  <RevisionUploadPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/review/acknowledge"
              element={
                <ProtectedRoute>
                  <ReviewAcknowledgePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/create-action"
              element={
                <ProtectedRoute>
                  <CreateActionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/schedule/:projectId"
              element={
                <ProtectedRoute>
                  <ProjectSchedulePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/schedule"
              element={
                <ProtectedRoute>
                  <ProjectSchedulePage projectId={1} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/planning"
              element={
                <ProtectedRoute>
                  <ProjectPlanningPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/execution"
              element={
                <ProtectedRoute>
                  <ProjectExecutionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/main"
              element={
                <ProtectedRoute>
                  <MainDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/builder"
              element={
                <ProtectedRoute>
                  <ProjectBuilderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/builder/:projectId"
              element={
                <ProtectedRoute>
                  <ProjectBuilderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/builder/:projectId/:mode"
              element={
                <ProtectedRoute>
                  <ProjectBuilderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/templates"
              element={
                <ProtectedRoute allowedRoles={['System Admin', 'Airport Manager']}>
                  <TemplateListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={['System Admin', 'Airport Manager']}>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/airports"
              element={
                <ProtectedRoute allowedRoles={['System Admin', 'Airport Manager']}>
                  <AirportManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sharepoint-libraries"
              element={
                <ProtectedRoute allowedRoles={['System Admin']}>
                  <SharePointLibrariesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/system-stats"
              element={
                <ProtectedRoute allowedRoles={['System Admin']}>
                  <SystemStatsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/system-health"
              element={
                <ProtectedRoute allowedRoles={['System Admin']}>
                  <SystemHealthPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-report"
              element={
                <ProtectedRoute allowedRoles={['System Admin', 'Exec Admin', 'Airport Manager']}>
                  <ProjectAuditReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/holidays"
              element={
                <ProtectedRoute allowedRoles={['System Admin']}>
                  <HolidayManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/templates/:templateId/edit"
              element={
                <ProtectedRoute allowedRoles={['System Admin']}>
                  <TemplateEditPage />
                </ProtectedRoute>
              }
            />
          </Routes>
          </AuthShell>
        </ModalOpenProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;

